"use client";

/**
 * Holds the visitor's cookie choice and tells the rest of the app what may load.
 *
 * The whole point is the DEFAULT: until a visitor says yes, `allows()` answers
 * false for everything optional. Nothing here loads a tracker — callers ask
 * before they mount one, so a script that was never rendered cannot set a
 * cookie. That is the difference between a consent banner and a cookie notice.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { ConsentCategory, ConsentChoices } from "./types";
import { clearCookiesFor } from "./cookies";
import { needsAsking, readConsent, writeConsent } from "./store";
import { EVERYTHING_ALLOWED, NOTHING_ALLOWED } from "./types";

interface ConsentApi {
  /** False when there is no provider above this component — the CMS content
   * was unreadable, so ConsentGateway rendered children bare. Distinct from a
   * refusal: nobody was ever asked, and callers that would otherwise record a
   * decision must not treat the default no as one. */
  configured: boolean;
  /** False until the stored choice has been read. Nothing consent-related should
   * paint before then, or a returning visitor sees a flash of the banner and a
   * new one sees a flash of the settings link. */
  ready: boolean;
  /** May this category's scripts load? Always false before a decision. */
  allows: (category: ConsentCategory) => boolean;
  /** True while the visitor still owes us an answer — the banner's cue. */
  mustAsk: boolean;
  /** The stored answers, for rendering the toggles. */
  choices: ConsentChoices;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (choices: ConsentChoices) => void;
  /** Re-open the panel so a visitor can change their mind, as promised. */
  reopen: () => void;
  panelOpen: boolean;
  closePanel: () => void;
}

const ConsentContext = createContext<ConsentApi | null>(null);

/** Nothing to do: with no provider there is no decision to record. */
function noConsentProvider(): void {
  return undefined;
}

export function ConsentProvider({
  offered,
  children,
}: {
  /** The categories the CMS currently offers. A new one re-opens the question. */
  offered: ConsentCategory[];
  children: React.ReactNode;
}) {
  const [choices, setChoices] = useState<ConsentChoices>(NOTHING_ALLOWED);
  const [mustAsk, setMustAsk] = useState(false);
  const [ready, setReady] = useState(false);
  /** The categories the visitor has actually been shown, from storage. */
  const [asked, setAsked] = useState<ConsentCategory[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);

  // Read on mount, never during render: the server has no localStorage, so
  // reading it in the initial state would make the server and client disagree.
  // Before this effect runs, nothing optional is allowed — the safe direction.
  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setChoices(stored.choices);
      setAsked(stored.asked);
    }
    setMustAsk(needsAsking(stored, offered));
    setReady(true);
  }, [offered]);

  const commit = useCallback(
    (next: ConsentChoices) => {
      setChoices(next);
      setAsked(offered);
      setMustAsk(false);
      setPanelOpen(false);
      writeConsent({ choices: next, asked: offered, decidedAt: Date.now() });
      // `next` covers every category the type knows, but `offered` is what the
      // visitor was actually shown. Anything else is recorded as stored, and
      // `allows()` below refuses it regardless — see the note there.
      // Withdrawal has to undo, not just stop. The scripts unmount on the next
      // render, but the cookies they already wrote would sit on the device
      // identifying the same person until they expired on their own.
      const refused = (Object.keys(next) as (keyof ConsentChoices)[]).filter(
        (category) => !next[category],
      );
      clearCookiesFor(refused);
    },
    [offered],
  );

  const api = useMemo<ConsentApi>(
    () => ({
      // A stored yes only counts for a category the visitor was ASKED about.
      // Accepting all writes `true` for every key the type has, including ones
      // no banner ever showed — so the day `functional` is added to the CMS,
      // everyone who once clicked "Accept all" would arrive pre-consented to a
      // question they were never asked. `mustAsk` would be true and the banner
      // up, and the tracker would already be loading behind it.
      configured: true,
      allows: (category) =>
        category === "necessary"
          ? true
          : asked.includes(category) && choices[category] === true,
      ready,
      mustAsk,
      choices,
      acceptAll: () => commit(EVERYTHING_ALLOWED),
      rejectAll: () => commit(NOTHING_ALLOWED),
      save: commit,
      reopen: () => setPanelOpen(true),
      panelOpen,
      closePanel: () => setPanelOpen(false),
    }),
    [asked, choices, commit, mustAsk, panelOpen, ready],
  );

  return (
    <ConsentContext.Provider value={api}>{children}</ConsentContext.Provider>
  );
}

/**
 * Outside a provider this reports "nothing allowed, nothing to ask". A tracker
 * rendered in a tree with no provider therefore stays off rather than defaulting
 * on, and a missing provider shows up as no analytics rather than as silent
 * tracking without consent.
 */
export function useConsent(): ConsentApi {
  return (
    useContext(ConsentContext) ?? {
      configured: false,
      allows: (category) => category === "necessary",
      // Ready, so a tree with no provider is not stuck rendering nothing.
      ready: true,
      mustAsk: false,
      choices: NOTHING_ALLOWED,
      acceptAll: noConsentProvider,
      rejectAll: noConsentProvider,
      save: noConsentProvider,
      reopen: noConsentProvider,
      panelOpen: false,
      closePanel: noConsentProvider,
    }
  );
}
